import {
  TbBuilding,
  TbId,
  TbKey,
  TbMail,
  TbPhone,
} from "react-icons/tb";
import { autoFormatDocument } from "../../../../Utils/formatters";

export const PIX_KEYS_LIMIT = 5;

export const PIX_KEY_TYPES = [
  {
    id: "email",
    label: "E-mail",
    icon: TbMail,
    placeholder: "email@exemplo.com",
    apiType: "email",
  },
  {
    id: "phone",
    label: "Celular",
    icon: TbPhone,
    placeholder: "(00) 00000-0000",
    apiType: "phone",
  },
  {
    id: "cpf",
    label: "CPF",
    icon: TbId,
    placeholder: "000.000.000-00",
    apiType: "cpf",
  },
  {
    id: "cnpj",
    label: "CNPJ",
    icon: TbBuilding,
    placeholder: "00.000.000/0000-00",
    apiType: "cnpj",
  },
  {
    id: "evp",
    label: "Chave aleatória",
    icon: TbKey,
    placeholder: "",
    apiType: "evp",
    autoGenerate: true,
  },
];

export const PIX_KEY_TYPE_LABELS = PIX_KEY_TYPES.reduce((acc, item) => {
  acc[item.id] = item.label;
  acc[item.apiType] = item.label;
  return acc;
}, {});

export const MOCK_PIX_KEYS = [
  {
    id: "mock-1",
    type: "email",
    value: "contato@empresa.com.br",
    status: "active",
    isPrimary: true,
    createdAt: "2026-01-12T10:30:00",
  },
  {
    id: "mock-2",
    type: "phone",
    value: "+5511987654321",
    status: "active",
    isPrimary: false,
    createdAt: "2026-02-18T14:15:00",
  },
  {
    id: "mock-3",
    type: "evp",
    value: "b7c8d9e0-f1a2-4b3c-9d8e-7f6a5b4c3d2e",
    status: "active",
    isPrimary: false,
    createdAt: "2026-03-05T09:00:00",
  },
];

export const getPixKeyTypeMeta = (type) =>
  PIX_KEY_TYPES.find((item) => item.id === type || item.apiType === type);

/** Bacen/PaySmart use ATIVA; carteira historically used active/confirmed. */
const INACTIVE_PIX_KEY_STATUSES = new Set([
  "inactive",
  "inativa",
  "deleted",
  "excluida",
  "excluída",
  "cancelled",
  "cancelada",
  "blocked",
  "bloqueada",
  "pending_deletion",
]);

export const normalizePixKeyStatus = (status) => {
  const raw = String(status || "active").trim().toLowerCase();
  if (raw === "ativa" || raw === "enabled" || raw === "habilitada") return "active";
  if (raw === "confirmada") return "confirmed";
  if (raw === "criada") return "created";
  if (raw === "inativa") return "inactive";
  if (raw === "excluida" || raw === "excluída") return "deleted";
  if (raw === "cancelada") return "cancelled";
  if (raw === "bloqueada") return "blocked";
  return raw || "active";
};

/** Prefer short display name (strip " · TYPE" suffixes from older API labels). */
export const shortPixAccountLabel = (label) => {
  const raw = String(label || "").trim();
  if (!raw) return "";
  return raw.split(" · ")[0].trim() || raw;
};

/**
 * Same source as Minhas chaves; exclude inactive, available-to-link, and keys linked away
 * from the viewing account (credit goes to the destination virtual).
 */
export const isSelectablePixKey = (key) => {
  const value = String(key?.value || "").trim();
  if (!value) return false;
  if (key?.availableToLink) return false;
  const accountId = String(key?.accountId || "").trim();
  const linkedAccountId = String(key?.linkedAccountId || "").trim();
  if (key?.linked && linkedAccountId && accountId && linkedAccountId !== accountId) {
    return false;
  }
  return !INACTIVE_PIX_KEY_STATUSES.has(normalizePixKeyStatus(key?.status));
};

export const normalizePixKey = (item = {}) => {
  const value = String(item.value || item.key_value || item.key || "").trim();
  const id = String(item.id || item.key_id || item.keyId || item.uuid || value || "");
  const accountId = String(item.account_id || item.accountId || "").trim();
  const linkedAccountId = String(
    item.linked_account_id || item.linkedAccountId || "",
  ).trim();
  const linked = Boolean(
    item.linked
      || item.Linked
      || (linkedAccountId && accountId && linkedAccountId !== accountId)
      || (linkedAccountId && (item.origin_account_id || item.originAccountId)),
  );
  return {
    id,
    accountId,
    type: item.type || item.key_type || item.keyType || "evp",
    value,
    status: normalizePixKeyStatus(item.status),
    isPrimary: Boolean(item.is_primary ?? item.primary ?? item.isPrimary),
    createdAt: item.created_at || item.createdAt || item.creation_date || item.creationDate || "",
    linked,
    availableToLink: Boolean(item.available_to_link || item.availableToLink),
    originAccountId: String(item.origin_account_id || item.originAccountId || "").trim() || null,
    originAccountLabel: shortPixAccountLabel(
      item.origin_account_label || item.originAccountLabel || "",
    ) || null,
    linkedAccountId: linkedAccountId || null,
    linkedAccountLabel: shortPixAccountLabel(
      item.linked_account_label || item.linkedAccountLabel || "",
    ) || null,
  };
};

export const formatPixKeyValue = (type, value = "") => {
  if (!value) return "—";

  if (type === "phone") {
    const digits = value.replace(/\D/g, "");
    const local = digits.startsWith("55") ? digits.slice(2) : digits;
    if (local.length === 11) {
      return `+55 (${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
    }
    return value;
  }

  if (type === "cpf" || type === "cnpj" || type === "document") {
    return autoFormatDocument(value.replace(/\D/g, ""));
  }

  if (type === "evp" && value.length > 24) {
    return `${value.slice(0, 8)}…${value.slice(-8)}`;
  }

  return value;
};

export const formatPixKeyDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const PIX_KEY_STATUS_META = {
  active: {
    label: "Ativa",
    className: "bg-emerald-50 text-emerald-700",
  },
  confirmed: {
    label: "Confirmada",
    className: "bg-emerald-50 text-emerald-700",
  },
  created: {
    label: "Criada",
    className: "bg-sky-50 text-sky-700",
  },
  pending: {
    label: "Pendente",
    className: "bg-amber-50 text-amber-800",
  },
  inactive: {
    label: "Inativa",
    className: "bg-gray-100 text-gray-600",
  },
  deleted: {
    label: "Excluída",
    className: "bg-red-50 text-red-700",
  },
  cancelled: {
    label: "Cancelada",
    className: "bg-red-50 text-red-700",
  },
  blocked: {
    label: "Bloqueada",
    className: "bg-red-50 text-red-700",
  },
};

export const getPixKeyStatusMeta = (status) => {
  const normalized = normalizePixKeyStatus(status);
  return (
    PIX_KEY_STATUS_META[normalized] || {
      label: String(status || "—"),
      className: "bg-gray-100 text-gray-600",
    }
  );
};

export const generateEvpKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `evp-${Date.now()}`;
};
